import type { Bicycle } from '../types';
import notEmpty from '../../core/util/notEmpty';
import filterBicycles from './filterBicycles';

export interface FilterSelection {
    make: string[];
    model: string[];
    modelYear: string[];
    type: string[];
    category: string[];
}

export const filterToBicycleFieldMap: {
    [key in keyof FilterSelection]: keyof Bicycle;
} = {
    make: 'manufacturerName',
    model: 'model',
    modelYear: 'manufacturingStartYear',
    type: 'type',
    category: 'category',
};

const range = (start: number, end: number) => {
    const list = [];
    for (let i = start; i <= end; i += 1) {
        list.push(i);
    }

    return list;
};

const filterSorter =
    (name: keyof FilterSelection) => (a: string, b: string) => {
        if (['make', 'model', 'type', 'category'].includes(name)) {
            return a.localeCompare(b);
        }

        return +b - +a;
    };

const collectValues = (vehicles: Bicycle[], name: keyof FilterSelection) => {
    return Array.from(
        new Set(
            vehicles
                .flatMap(vehicle => {
                    if (name === 'modelYear') {
                        if (
                            !vehicle.manufacturingEndYear &&
                            !vehicle.manufacturingStartYear
                        ) {
                            return null;
                        }
                        if (!vehicle.manufacturingEndYear) {
                            return `${vehicle.manufacturingStartYear}`;
                        }

                        if (!vehicle.manufacturingStartYear) {
                            return `${vehicle.manufacturingEndYear}`;
                        }
                        return range(
                            vehicle.manufacturingStartYear,
                            vehicle.manufacturingEndYear
                        ).map(item => `${item}`);
                    }
                    return `${vehicle[filterToBicycleFieldMap[name]]}`;
                })
                .filter(notEmpty)
        )
    ).sort(filterSorter(name));
};

const getVehicleFilterValues = (
    vehicles: Bicycle[],
    selectedFilters: FilterSelection
) => {
    const filters = Object.entries(selectedFilters);
    const values = filters.map(([name]) => {
        const otherFilters = filters.filter(
            ([filterName]) => filterName !== name
        );
        const [, vehiclesFilteredByOthers] = filterBicycles(vehicles, {
            ...Object.fromEntries(otherFilters),
            [name]: [],
        } as FilterSelection);

        return [
            name,
            Array.from(
                new Set([
                    ...collectValues(
                        vehiclesFilteredByOthers,
                        name as keyof FilterSelection
                    ),
                ])
            ),
        ] as [keyof FilterSelection, string[]];
    });
    return Object.fromEntries(values) as unknown as FilterSelection;
};

export default getVehicleFilterValues;
